import { fitMapImage, zoomForScale } from '../../lib/print-layout';

/** Resolve when the map reports `idle` (all tiles/sources rendered), or after timeoutMs. */
export function waitForIdle(map, timeoutMs = 10000) {
  return new Promise((resolve) => {
    let done = false;
    const finish = () => { if (done) return; done = true; clearTimeout(t); map.off?.('idle', finish); resolve(); };
    const t = setTimeout(finish, timeoutMs);
    map.once('idle', finish);
    map.triggerRepaint?.(); // guarantees an `idle` even when nothing was pending
  });
}

/**
 * Capture the live map canvas for the print page.
 *
 * The canvas must have been created with `preserveDrawingBuffer` (SurveyMap does) or toDataURL
 * returns a blank image. To honour a chosen scale the camera is moved to the matching zoom
 * (pitch 0, padding 0), the map is left to reach `idle`, the canvas is read and the camera is
 * put back.
 *
 * @param map          MapLibre map
 * @param denominator  fixed scale (1:N) or null to keep the current zoom
 * @param frame        {w,h} of the page's map frame in css px (to work out magnification)
 * @returns {{ dataUrl, view:{center,zoom,bearing,k}, mapW, mapH, lat }}
 */
export async function captureMap(map, { denominator = null, frame }) {
  const saved = {
    center: map.getCenter(), zoom: map.getZoom(), bearing: map.getBearing(), pitch: map.getPitch(),
    padding: map.getPadding ? map.getPadding() : null,
  };
  const container = map.getContainer();
  const mapW = container.clientWidth;
  const mapH = container.clientHeight;
  const { k } = fitMapImage({ mapW, mapH, frameW: frame.w, frameH: frame.h });
  try {
    if (saved.padding && Object.values(saved.padding).some((v) => v)) map.setPadding({ top: 0, left: 0, right: 0, bottom: 0 });
    const target = { center: saved.center, bearing: saved.bearing, pitch: 0, zoom: saved.zoom };
    if (denominator) target.zoom = zoomForScale(denominator, saved.center.lat, k);
    map.jumpTo(target);
    await waitForIdle(map);
    const dataUrl = map.getCanvas().toDataURL('image/png');
    return {
      dataUrl,
      mapW,
      mapH,
      lat: saved.center.lat,
      view: { center: [saved.center.lng, saved.center.lat], zoom: map.getZoom(), bearing: saved.bearing, k },
    };
  } finally {
    map.jumpTo({ center: saved.center, zoom: saved.zoom, bearing: saved.bearing, pitch: saved.pitch });
    if (saved.padding && Object.values(saved.padding).some((v) => v)) map.setPadding(saved.padding);
  }
}
